import { MutableRefObject } from 'react';
import * as PIXI from "pixi.js"

import * as FBRConstants from '../FBRConstants';
import * as FBRTypes from '../FBRTypes';

function scaleVideoSprite(videoSprite: PIXI.Sprite) {
	let scale = FBRConstants.STAGE_WIDTH/videoSprite.width;
	if(videoSprite.height > videoSprite.width) {
			scale = FBRConstants.STAGE_HEIGHT/videoSprite.height;
	}
	if(videoSprite.width*scale < FBRConstants.STAGE_WIDTH) {
		scale = FBRConstants.STAGE_WIDTH/videoSprite.width;
	} else if (videoSprite.height*scale < FBRConstants.STAGE_HEIGHT) {
		scale = FBRConstants.STAGE_HEIGHT/videoSprite.height;
	}
	videoSprite.scale = new PIXI.Point(scale, scale);
	return videoSprite;
}

function createVideoSprite(videoElement: HTMLVideoElement) {
	const videoResource = new PIXI.VideoResource(videoElement);
	videoResource.source.loop = true;
	videoResource.source.muted = true;
	const videoSprite = new PIXI.Sprite(PIXI.Texture.from(videoResource.source));
	videoSprite.visible = false;
	videoSprite.anchor.set(0.5, 0.5);
	return scaleVideoSprite(videoSprite);
}

const createVideoStatusContainer = (
	otherVideos: HTMLVideoElement[],
	successVideos: HTMLVideoElement[],
	failVideos: HTMLVideoElement[],
	pixiAppRef: MutableRefObject<PIXI.Application | undefined>,
	globalState: MutableRefObject<FBRTypes.GlobalState>,
) => {
	const splashScreenContainer = new PIXI.Container();
	const currentVideoContainer = new PIXI.Container();
	currentVideoContainer.x = FBRConstants.STAGE_WIDTH/2;
	currentVideoContainer.y = FBRConstants.STAGE_HEIGHT/2;
	splashScreenContainer.addChild(currentVideoContainer);	
	
	const otherSprites: PIXI.Sprite[] = [];
	const successSprites: PIXI.Sprite[] = [];
	const failSprites: PIXI.Sprite[] = [];

	//construct video sprites and add to video container	
	otherVideos.forEach( (videoElement) => {
		const videoSprite = createVideoSprite(videoElement);
		otherSprites.push(videoSprite);
		currentVideoContainer.addChild(videoSprite);
	});
	
	successVideos.forEach( (videoElement) => {
		const videoSprite = createVideoSprite(videoElement);
		successSprites.push(videoSprite);
		currentVideoContainer.addChild(videoSprite);
	});
	
	failVideos.forEach( (videoElement) => {
		const videoSprite = createVideoSprite(videoElement);
		failSprites.push(videoSprite);
		currentVideoContainer.addChild(videoSprite);
	});

	const _hideAllVideoSprites = () => {
		otherSprites.forEach( (videoSprite) => {
			videoSprite.visible = false;
		});
		successSprites.forEach( (videoSprite) => {
			videoSprite.visible = false;
		});
		failSprites.forEach( (videoSprite) => {
			videoSprite.visible = false;
		});
	}

	const _update = (() => {
		let totalActionsAtLastUpdate = -1;
		let totalCorrectActionsAtLastUpdate = -1;
		let totalIncorrectActionsAtLastUpdate = -1;
		let statusState = 'initial';
		return () => {
		const currentTotalActions = globalState.current.totalCorrectActions + globalState.current.totalIncorrectActions;
		if(totalActionsAtLastUpdate == currentTotalActions) {
			//console.log('no new actions taken, no updates needed, aborting');
			return;
		}
		//else continue since there have been new actions
		if(currentTotalActions > FBRConstants.VIDEO_STATE_WARMUP_THRESHOLD) {
			//player has made some moves
			if(globalState.current.maxSequentialCorrectActions > FBRConstants.VIDEO_STATE_CORRECT_ACTIONS_INITIAL_THRESHOLD) {
				if(
					statusState != 'success' 
					|| globalState.current.totalCorrectActions > totalCorrectActionsAtLastUpdate + FBRConstants.VIDEO_STATE_CORRECT_ACTIONS_STREAK_THRESHOLD
				) {
					statusState = 'success';
					const newSpriteIndex = Math.floor(successSprites.length*Math.random());
					//console.log(`successSprites index = ${newSpriteIndex}`);
					_hideAllVideoSprites();
					successSprites[newSpriteIndex].visible = true;
					totalCorrectActionsAtLastUpdate = globalState.current.totalCorrectActions;
				}
			} else if(globalState.current.maxSequentialIncorrectActions > FBRConstants.VIDEO_STATE_INCORRECT_ACTIONS_INITIAL_THRESHOLD) {
				if(
					statusState != 'fail' 
					|| globalState.current.totalIncorrectActions > totalIncorrectActionsAtLastUpdate + FBRConstants.VIDEO_STATE_INCORRECT_ACTIONS_STREAK_THRESHOLD
				) {
					statusState = 'fail';
					const newSpriteIndex = Math.floor(failSprites.length*Math.random());
					//console.log(`failSprites index = ${newSpriteIndex}`);
					_hideAllVideoSprites();
					failSprites[newSpriteIndex].visible = true;
					totalIncorrectActionsAtLastUpdate = globalState.current.totalIncorrectActions;
				}
			}
		} else if(statusState != 'neutral') {
			//player hasnt yet made many moves
			statusState = 'neutral';
			_hideAllVideoSprites();
			otherSprites[0].visible = true;
		}
		totalActionsAtLastUpdate = currentTotalActions;
		}
	})();
	
	return {
		container: splashScreenContainer,
		update: _update,
	};
};

export default createVideoStatusContainer;
